import Papa from 'papaparse'
import * as XLSX from 'xlsx'

export const REQUIRED_IMPORT_HEADERS = [
  'firstname',
  'lastname',
  'email',
  'multi_choice_gift_card',
  'amount',
  'scheduled_at'
]

export interface ImportRow {
  firstname: string
  lastname: string
  email: string
  multi_choice_gift_card: string
  amount: string
  scheduled_at: string
  program: string
  [key: string]: string
}

export interface ParseImportResult {
  headers: string[]
  data: ImportRow[]
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string

export function isExcelFile(file: File): boolean {
  return /\.(xlsx|xls)$/i.test(file.name)
}

export function isCsvFile(file: File): boolean {
  return /\.csv$/i.test(file.name)
}

export function isSupportedImportFile(file: File): boolean {
  return isExcelFile(file) || isCsvFile(file)
}

function normalizeHeaders(headers: string[]): string[] {
  return headers.map(header => header.trim())
}

function validateHeaders(headers: string[], t: TranslateFn): void {
  const missingHeaders = REQUIRED_IMPORT_HEADERS.filter(header => !headers.includes(header))
  if (missingHeaders.length > 0) {
    throw new Error(t('gifts.uploadDialog.errors.missingHeaders', { fields: missingHeaders.join(', ') }))
  }
}

function normalizeRow(row: Record<string, unknown>): ImportRow {
  const normalized: ImportRow = {
    firstname: '',
    lastname: '',
    email: '',
    multi_choice_gift_card: '',
    amount: '',
    scheduled_at: '',
    program: ''
  }

  for (const [key, value] of Object.entries(row)) {
    const field = key.trim()
    if (value == null || value === '') {
      normalized[field] = ''
      continue
    }

    normalized[field] = String(value).trim()
  }

  if (normalized.amount) {
    const parsedAmount = parseFloat(normalized.amount.replace(',', '.'))
    if (!Number.isNaN(parsedAmount)) {
      normalized.amount = String(Math.round(parsedAmount))
    }
  }

  return normalized
}

function filterEmptyRows(rows: ImportRow[]): ImportRow[] {
  return rows.filter(row => Object.values(row).some(value => value !== ''))
}

function parseCsvFile(file: File, t: TranslateFn): Promise<ParseImportResult> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        try {
          const headers = normalizeHeaders(results.meta.fields || [])
          validateHeaders(headers, t)

          const data = filterEmptyRows(((results.data || []) as Record<string, unknown>[]).map(normalizeRow))
          if (!data.length) {
            throw new Error(t('gifts.uploadDialog.errors.noData'))
          }

          resolve({ headers, data })
        } catch (error) {
          reject(error)
        }
      },
      error: () => {
        reject(new Error(t('gifts.uploadDialog.errors.readError')))
      }
    })
  })
}

function parseExcelFile(file: File, t: TranslateFn): Promise<ParseImportResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        const buffer = event.target?.result
        if (!buffer) {
          throw new Error(t('gifts.uploadDialog.errors.readError'))
        }

        const workbook = XLSX.read(buffer, { type: 'array', cellDates: true })
        const sheetName = workbook.SheetNames[0]
        if (!sheetName) {
          throw new Error(t('gifts.uploadDialog.errors.noData'))
        }

        const sheet = workbook.Sheets[sheetName]
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          raw: false,
          defval: ''
        })

        if (!rows.length) {
          throw new Error(t('gifts.uploadDialog.errors.noData'))
        }

        const headers = normalizeHeaders(Object.keys(rows[0]))
        validateHeaders(headers, t)

        const data = filterEmptyRows(rows.map(normalizeRow))
        if (!data.length) {
          throw new Error(t('gifts.uploadDialog.errors.noData'))
        }

        resolve({ headers, data })
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => {
      reject(new Error(t('gifts.uploadDialog.errors.readError')))
    }

    reader.readAsArrayBuffer(file)
  })
}

export async function parseImportFile(file: File, t: TranslateFn): Promise<ParseImportResult> {
  if (!isSupportedImportFile(file)) {
    throw new Error(t('gifts.uploadDialog.errors.unsupportedFormat'))
  }

  if (isExcelFile(file)) {
    return parseExcelFile(file, t)
  }

  return parseCsvFile(file, t)
}

export function downloadExcelTemplate(): void {
  const headers = [...REQUIRED_IMPORT_HEADERS]
  const worksheet = XLSX.utils.aoa_to_sheet([headers])
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Dotations')
  XLSX.writeFile(workbook, 'template.xlsx')
}
